import React, { useEffect, useRef } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** If true, modal becomes a bottom sheet on small screens (below md breakpoint). */
  bottomSheetMobile?: boolean;
}

const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  maxWidth = 'md',
  bottomSheetMobile = false
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      
      // Focus the modal when it opens
      if (modalRef.current) {
        modalRef.current.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
  };

  const sheetClasses = bottomSheetMobile ? 'items-end md:items-center' : 'items-center';

  const panelRounded = bottomSheetMobile ? 'rounded-t-lg md:rounded-lg' : 'rounded-lg';
  const translateClass = bottomSheetMobile
    ? isOpen
      ? 'translate-y-0 md:scale-100'
      : 'translate-y-full md:scale-95'
    : isOpen
      ? 'scale-100'
      : 'scale-95';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 backdrop-blur-sm transition-all duration-300"
        onClick={onClose}
      />
      
      {/* Modal container */}
      <div className={`flex min-h-full justify-center p-4 ${sheetClasses}`}>
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`relative bg-white text-text ${panelRounded} shadow-xl w-full ${maxWidthClasses[maxWidth]} transform transition-all duration-300 ease-out ${translateClass} ${isOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-accent/40">
            <h2 className="text-xl font-semibold text-text">{title}</h2>
            <button
              onClick={onClose}
              className="text-text/60 hover:text-text focus:outline-none focus:ring-2 focus:ring-primary rounded-md p-1"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal; 